import { Component, Input, OnInit, Output, EventEmitter, OnDestroy} from '@angular/core';
import { CnafUserService } from '@cnaf/ng-cnaf-commons';
import { FormGroup, FormControl, ValidatorFn, ValidationErrors, ControlContainer, FormRecord } from '@angular/forms';
import { Subscription } from 'rxjs';
import { RessourcesModel, CodesRessourcesModel, ConfigurationRessourceModel, RessourcesForm } from './models/ressources.model';
import { RessourcesPropertiesService } from './services/ressources-properties.service';
import { CcdStringUtils } from '../../../services/ccd-string.utils';
import { CcdRubriqueMontantModel, RubriqueMontantForm } from '@cnaf.usagers.appli/cnaf-commons-dral';

@Component({
  selector: 'app-ressources',
  templateUrl: './ressources.component.html',
  styleUrls: ['./ressources.component.css']
})
export class RessourcesComponent implements OnInit, OnDestroy {

  /**
   * Nom du form group
   */
  @Input() name : string;
  
  /**
   * Profil de la personne
   */
  @Input() profil : number;

  /**
   * Notification au parent des dernieres valeurs saisies dans le composant
   */
  @Output() ressourceModelEvent = new EventEmitter<RessourcesModel>();

  /**
   * Bean metier associé au composant
   */
  @Input() ressourceModel: RessourcesModel = new RessourcesModel();

  /**
   * Map de ressources
   */
  dataRessArray = new Map<string, CcdRubriqueMontantModel>();

  /**
   * Messages d'erreurs à afficher dans les montants
   */
  montantMapErreurs = new Map<string, string>();

  /**
   * Liste des subscribers
   */
  subscribers : Array<Subscription> = new Array();

  /**
   * Type de ressources (propriétés)
   */
  typeRessources : Array<ConfigurationRessourceModel>;

  // Gestion du formulaire
  parentForm : FormGroup;

  ressourcesForm = new FormGroup<RessourcesForm>({
    aucunRevenu : new FormControl<boolean>(false, {nonNullable:true}),
    rubriquesMontantRecord : new FormRecord<FormGroup<RubriqueMontantForm>>({})
  });

  /**
   * Objet contenant les codes associés aux ressources
   */
  codesRessources : CodesRessourcesModel;

  /**
   * 
   * @param controlContainer 
   * @param cnafUserService 
   */
  constructor(
    private readonly controlContainer: ControlContainer,
    private readonly cnafUserService: CnafUserService) { 
  }

  /**
   * Initialisation du composant
   */
  ngOnInit(): void {
    
    this.codesRessources = RessourcesPropertiesService.genererCodesRessources(this.profil);

    this.parentForm = this.controlContainer?.control as FormGroup;
    this.parentForm.addControl(this.name, this.ressourcesForm);

    const date = this.transformeDate(this.cnafUserService.dateEnvCaffr);
    this.typeRessources = RessourcesPropertiesService.getRessourcesProperties(date, this.profil);
    this.montantMapErreurs = RessourcesPropertiesService.getMapMessagesErreurMontants();
    
    this.typeRessources.forEach(data => {
      this.dataRessArray.set(data.code, new CcdRubriqueMontantModel());
    })
  
    this.ressourcesForm.setValidators(this.validationCasesACochees());

    this.mapModelToForm();

    // A supprimer plus tard lorsque le parent utilisera le double data binding
    this.subscribers.push(
      this.ressourcesForm.valueChanges.subscribe(data => {
          this.mettreAJourRubriquesSiAuCunRevenu();
          this.mapFormToModel();
          this.ressourceModelEvent.emit(this.ressourceModel);
      })
    );

    this.ressourceModelEvent.emit(this.ressourceModel);
  }

  /**
   * desactivation des rubriques si Aucun revenu coché
   */
  mettreAJourRubriquesSiAuCunRevenu() : void {
      if (
          (this.ressourceModel.aucunRevenuDefini !== this.ressourcesForm.getRawValue().aucunRevenu) && 
          this.ressourcesForm.getRawValue().aucunRevenu) {

        this.dataRessArray.forEach((rubriqueModel, key) => {
          const newRub = new CcdRubriqueMontantModel();
          newRub.isActive = false;
          newRub.erreurs = rubriqueModel.erreurs;
          newRub.montant = '';
          this.dataRessArray.set(key, newRub);
      })
    }
  }

  /**
   * Binding formulaire --> objet model
   */
  mapFormToModel() : void {
    this.ressourceModel = new RessourcesModel();

    this.ressourceModel.aucunRevenuDefini = this.ressourcesForm.getRawValue().aucunRevenu;

    this.ressourceModel.chomagePreretraitreDefini = this.dataRessArray.get(this.codesRessources.allocationChomage).isActive;
    this.ressourceModel.montantChomagePreretraitre = CcdStringUtils.stringToNumber(this.dataRessArray.get(this.codesRessources.allocationChomage).montant);

    this.ressourceModel.salaireDefini = this.dataRessArray.get(this.codesRessources.salaire).isActive;
    this.ressourceModel.montantSalaire = CcdStringUtils.stringToNumber(this.dataRessArray.get(this.codesRessources.salaire).montant);
    
    this.ressourceModel.retraitrePensionDefini = this.dataRessArray.get(this.codesRessources.retraitrePensionImposable).isActive;
    this.ressourceModel.montantRetraitrePension = CcdStringUtils.stringToNumber(this.dataRessArray.get(this.codesRessources.retraitrePensionImposable).montant);

    this.ressourceModel.pensionAlimentaireDefini = this.dataRessArray.get(this.codesRessources.pensionAlimentaireRecue).isActive;
    this.ressourceModel.montantPensionAlimentaire = CcdStringUtils.stringToNumber(this.dataRessArray.get(this.codesRessources.pensionAlimentaireRecue).montant);

    this.ressourceModel.revenuIndependentAnneeMoins2Defini = this.dataRessArray.get(this.codesRessources.revenuTravailleurIndependant).isActive;
    this.ressourceModel.montantRevenuIndependentAnneeMoins2 = CcdStringUtils.stringToNumber(this.dataRessArray.get(this.codesRessources.revenuTravailleurIndependant).montant);
  }

  /**
   * Binding objet model --> formulaire
   */
  mapModelToForm() : void {
    this.ressourcesForm.controls.aucunRevenu.patchValue(this.ressourceModel.aucunRevenuDefini);

    this.dataRessArray.get(this.codesRessources.allocationChomage).isActive = this.ressourceModel.chomagePreretraitreDefini;
    this.dataRessArray.get(this.codesRessources.allocationChomage).montant = CcdStringUtils.numberToString(this.ressourceModel.montantChomagePreretraitre);

    this.dataRessArray.get(this.codesRessources.salaire).isActive = this.ressourceModel.salaireDefini;
    this.dataRessArray.get(this.codesRessources.salaire).montant = CcdStringUtils.numberToString(this.ressourceModel.montantSalaire);

    this.dataRessArray.get(this.codesRessources.retraitrePensionImposable).isActive = this.ressourceModel.retraitrePensionDefini;
    this.dataRessArray.get(this.codesRessources.retraitrePensionImposable).montant = CcdStringUtils.numberToString(this.ressourceModel.montantRetraitrePension);

    this.dataRessArray.get(this.codesRessources.pensionAlimentaireRecue).isActive = this.ressourceModel.pensionAlimentaireDefini;
    this.dataRessArray.get(this.codesRessources.pensionAlimentaireRecue).montant = CcdStringUtils.numberToString(this.ressourceModel.montantPensionAlimentaire);
    
    this.dataRessArray.get(this.codesRessources.revenuTravailleurIndependant).isActive = this.ressourceModel.revenuIndependentAnneeMoins2Defini;
    this.dataRessArray.get(this.codesRessources.revenuTravailleurIndependant).montant = CcdStringUtils.numberToString(this.ressourceModel.montantRevenuIndependentAnneeMoins2);
  }

  /**
   * A la destruction du composant (implémentation de OnDestroy) : 
   * On vide les subscribtions effectuées à l'init
   */
  ngOnDestroy(): void {
    this.parentForm.removeControl(this.name);
    this.subscribers.forEach(sub => sub.unsubscribe());
  }

  /**
   * Transformation de la date
   */
  transformeDate = function (dateAAAAMMJJ) {
    const year = dateAAAAMMJJ.substring(0, 4);
    const month = dateAAAAMMJJ.substring(4, 6);
    const day = dateAAAAMMJJ.substring(6, 8);
    // Le constructeur de date pour le mois est en base 0 donc on retire 1 pour faire correspondre nos résultats.
    return new Date(year, month - 1, day);
  }

  /**
   * Validateur custom pour gerer le cas aucune case a cocher
   * Ce validateur renvoie une erreur si aucune case n'est cochée
   */
  validationCasesACochees(): ValidatorFn {
    
    return (controls: FormGroup<RessourcesForm>): ValidationErrors | null => {
      let auMoinsUneCaseCochee : boolean = controls.getRawValue().aucunRevenu;
      this.dataRessArray.forEach(data => {
        auMoinsUneCaseCochee = data.isActive || auMoinsUneCaseCochee;
      });

      const msg = "aucune rubrique active";
      return !auMoinsUneCaseCochee ? {aucuneRubriqueActive: msg} : null;
    };

  }
}
